import React, { useEffect, useRef, useState } from 'react';
import { CharacterDefinition } from '@hexcrawl/shared-utils';
import { assemblePreviewFrame } from '../../../../sprites/spriteAssembler.ts';

const DEBOUNCE_MS = 400;

interface Props {
  definition: CharacterDefinition | null;
  size?: number;
  // When true, shows an "Updating…" overlay without triggering a re-assemble.
  // Used by callers that manage their own regeneration state externally.
  loading?: boolean;
}

export const AppearancePreview: React.FC<Props> = ({ definition, size = 200, loading: externalLoading = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [assembling, setAssembling] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);
  const loading = assembling || externalLoading;
  const defKey = definition ? JSON.stringify(definition) : null;

  useEffect(() => {
    if (!definition) return;

    const timeout = setTimeout(async () => {
      setAssembling(true);
      try {
        const frame = await assemblePreviewFrame(definition);
        const canvas = canvasRef.current;
        if (canvas) {
          canvas.width = frame.width;
          canvas.height = frame.height;
          const ctx = canvas.getContext('2d')!;
          ctx.clearRect(0, 0, frame.width, frame.height);
          ctx.drawImage(frame, 0, 0);
          setHasFrame(true);
        }
        frame.close();
      } catch (e) {
        console.warn('[AppearancePreview] Assembly failed:', e);
      } finally {
        setAssembling(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defKey]);

  return (
    <div
      style={{
        width: size,
        height: size,
        background: '#0d0d1a',
        border: '1px solid #444',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          imageRendering: 'pixelated',
          maxWidth: '100%',
          maxHeight: '100%',
          opacity: loading ? 0.5 : 1,
          transition: 'opacity 0.1s',
          display: hasFrame ? 'block' : 'none',
        }}
      />
      {!hasFrame && (
        <span style={{ color: loading ? '#aaa' : '#555', fontSize: 12 }}>
          {loading ? 'Loading…' : 'Preview'}
        </span>
      )}
      {loading && hasFrame && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.3)',
            fontSize: 11,
            color: '#ccc',
          }}
        >
          Updating…
        </div>
      )}
    </div>
  );
};
